if (!customElements.get('cc-carousel')) {
	class CCCarousel extends HTMLElement {
		constructor() {
			super();
			this.prepare();
		}

		get() {
			this.prepare();
			return this;
		}

		prepare() {
			if (this.prepared) {
				return;
			}

			this.prepared = true;
			this.scrollOuter = this.querySelector('.cc-carousel__scroll-outer');
			this.scrollArea = this.querySelector('.cc-carousel__scroll-area');
			this.prevButton = this.querySelector('.cc-carousel-button--prev');
			this.nextButton = this.querySelector('.cc-carousel-button--next');
			this.buttonContainer = this.querySelector('.cc-carousel-buttons');
			this.allowMouseDrag = this.hasAttribute('allow-mouse-drag');
			this.selectSingle = this.hasAttribute('select-single');
			this.loopPreferred = this.hasAttribute('loop');
			this.setItemClasses = this.hasAttribute('set-item-classes');
			this.ltr = !document.querySelector('html[dir=rtl]');
			this.scrollArea.scrollTo({ top: 0, left: 0, behavior: 'instant' });
			this.init();
		}

		init() {
			this.selectedItemIndex = 0;
			this.flexDirection = getComputedStyle(this).getPropertyValue('--carousel-direction');

			if (this.scrollArea.childElementCount === 0 || this.flexDirection === 'column' || this.flexDirection === 'none') {
				return;
			}

			if (this.scrollArea.childElementCount === 1) {
				this.loop = false;
				this.classList.add('cc-carousel--at-start', 'cc-carousel--at-end');
				if (this.setItemClasses) {
					this.scrollArea.firstElementChild.classList.add('cc-carousel__current-item', 'cc-carousel__visible-item');
				}
				if (this.prevButton) {
					this.prevButton.disabled = true;
				}
				if (this.nextButton) {
					this.nextButton.disabled = true;
				}
				return;
			}

			this.loop = this.loopPreferred;
			this.scrollNextSearchOffset = this.scrollArea.firstElementChild.clientWidth / 8;
			this.scrollArea.addEventListener('scroll', this.onScroll.bind(this));

			if (this.prevButton) {
				this.boundPrev = this.prev.bind(this);
				this.prevButton.addEventListener('click', this.boundPrev);
			}

			if (this.nextButton) {
				this.boundNext = this.next.bind(this);
				this.nextButton.addEventListener('click', this.boundNext);
			}

			if (this.allowMouseDrag) {
				this.boundReleaseDrag = this.releaseDrag.bind(this);
				this.boundDrag = this.drag.bind(this);
				this.boundOnMouseDown = this.onMouseDown.bind(this);
				this.scrollOuter.addEventListener('mousedown', this.boundOnMouseDown);
			}

			this.whenStateSettled();
		}

		onScroll() {
			clearTimeout(this.onScrollTimeoutId);
			this.onScrollTimeoutId = setTimeout(() => {
				if (this.selectSingle && !this.scrollingForVisibility) {
					this.selectFirstVisibleItem();
				}
				this.whenStateSettled();
				this.scrollingForVisibility = false;
			}, 50);
		}

		onMouseDown(event) {
			this.dragLastX = event.clientX;
			this.dragFirstX = event.clientX;
			this.classList.add('cc-carousel--mousedown');
			document.addEventListener('mousemove', this.boundDrag);
			document.addEventListener('mouseup', this.boundReleaseDrag);
		}

		itemOffsetStart(item) {
			return this.ltr ? item.offsetLeft : this.scrollArea.clientWidth - item.offsetLeft - item.clientWidth;
		}

		itemIsWithinScrollView(item, scrollLeftOverride) {
			const scrollLeft = scrollLeftOverride !== undefined ? scrollLeftOverride : Math.abs(this.scrollArea.scrollLeft);
			const scrollRight = scrollLeft + this.scrollArea.clientWidth;
			const itemStart = this.itemOffsetStart(item) + 1;
			const itemEnd = itemStart + item.clientWidth - 3;
			return itemStart >= scrollLeft && itemEnd <= scrollRight;
		}

		drag(event) {
			event.preventDefault();
			if (!this.inDrag) {
				this.classList.add('cc-carousel--in-drag');
				this.inDrag = true;
			}
			this.scrollArea.scrollLeft = this.scrollArea.scrollLeft + (this.dragLastX - event.clientX);
			this.dragLastX = event.clientX;
		}

		releaseDrag(event) {
			if (this.inDrag) {
				this.inDrag = false;
				this.classList.remove('cc-carousel--in-drag');
				document.removeEventListener('mouseup', this.boundReleaseDrag);
				if (Math.abs(this.dragFirstX - event.clientX) < 20) {
					document.elementsFromPoint(event.clientX, event.clientY).forEach((element) => {
						if (element.tagName === 'A') {
							setTimeout(() => element.click(), 10);
						}
					});
				}
			}
			document.removeEventListener('mousemove', this.boundDrag);
			this.classList.remove('cc-carousel--mousedown');
		}

		prev() {
			let item = null;
			if (this.selectSingle) {
				item = this.findCurrentItem().previousElementSibling;
			} else {
				const targetScroll = Math.max(0, Math.abs(this.scrollArea.scrollLeft) - this.scrollArea.clientWidth);
				for (const child of this.scrollArea.children) {
					if (this.itemIsWithinScrollView(child, targetScroll)) {
						item = child;
						break;
					}
				}
			}
			if (this.loop && !item) {
				item = this.scrollArea.lastElementChild;
			}
			if (item) {
				this.selectItem(item);
			}
		}

		next() {
			let item = null;
			if (this.selectSingle) {
				item = this.findCurrentItem().nextElementSibling;
			} else {
				let foundCurrent = false;
				for (const child of this.scrollArea.children) {
					if (this.itemIsWithinScrollView(child)) {
						foundCurrent = true;
					} else if (foundCurrent) {
						item = child;
						break;
					}
				}
			}
			if (this.loop && !item) {
				item = this.scrollArea.firstElementChild;
			}
			if (item) {
				this.selectItem(item);
			}
		}

		findItemAfter(offset) {
			for (let index = 0; index < this.scrollArea.children.length; index += 1) {
				if (this.itemOffsetStart(this.scrollArea.children[index]) > offset) {
					return this.scrollArea.children[index];
				}
			}
			return this.scrollArea.lastElementChild;
		}

		findCurrentItem(visibleOnly) {
			if (this.selectSingle && !visibleOnly) {
				return this.scrollArea.children[this.selectedItemIndex];
			}
			const firstOffset = this.ltr ? this.scrollArea.firstElementChild.offsetLeft : 0;
			const scrollPosition = Math.abs(this.scrollArea.scrollLeft) - this.scrollNextSearchOffset + firstOffset;
			return this.findItemAfter(scrollPosition);
		}

		selectItem(item) {
			if (this.selectSingle) {
				let index = 0;
				let sibling = item;
				while ((sibling = sibling.previousElementSibling)) {
					index += 1;
				}
				this.selectedItemIndex = index;
			}

			if (!this.itemIsWithinScrollView(item)) {
				const left = item.offsetLeft - this.scrollArea.firstElementChild.offsetLeft;
				this.scrollingForVisibility = true;
				this.scrollArea.scrollTo({ top: 0, left, behavior: 'smooth' });
			}

			this.whenStateSettled();
		}

		selectFirstVisibleItem() {
			this.selectItem(this.findCurrentItem(true));
		}

		whenStateSettled() {
			clearTimeout(this.whenStateSettledTimeoutId);
			this.whenStateSettledTimeoutId = setTimeout(() => {
				const scrollLeft = Math.abs(Math.floor(this.scrollArea.scrollLeft));
				const atStart = scrollLeft <= 0;
				const atEnd = scrollLeft + this.scrollArea.clientWidth + 2 > this.scrollArea.scrollWidth;
				const currentItem = this.findCurrentItem();

				this.dispatchEvent(new CustomEvent('cc-carousel:selected', {
					bubbles: true,
					cancelable: false,
					detail: { currentItem },
				}));

				for (const child of this.scrollArea.children) {
					const isVisible = this.itemIsWithinScrollView(child);
					if (this.setItemClasses) {
						child.classList.toggle('cc-carousel__current-item', child === currentItem);
						child.classList.toggle('cc-carousel__visible-item', isVisible);
					}
				}

				this.classList.toggle('cc-carousel--at-start', atStart);
				this.classList.toggle('cc-carousel--at-end', atEnd);

				if (!this.prevButton && !this.nextButton) {
					return;
				}

				if (this.selectSingle) {
					this.buttonContainer?.classList.add('cc-carousel-buttons--show');
					if (!this.loop) {
						if (this.prevButton) {
							currentItem.previousElementSibling
								? this.prevButton.removeAttribute('disabled')
								: (this.prevButton.disabled = true);
						}
						if (this.nextButton) {
							currentItem.nextElementSibling
								? this.nextButton.removeAttribute('disabled')
								: (this.nextButton.disabled = true);
						}
					}
				} else if (atStart && atEnd) {
					this.buttonContainer?.classList.remove('cc-carousel-buttons--show');
					if (this.prevButton) {
						this.prevButton.disabled = true;
					}
					if (this.nextButton) {
						this.nextButton.disabled = true;
					}
				} else {
					this.buttonContainer?.classList.add('cc-carousel-buttons--show');
					if (this.prevButton) {
						this.loop || !atStart
							? this.prevButton.removeAttribute('disabled')
							: (this.prevButton.disabled = true);
					}
					if (this.nextButton) {
						this.loop || !atEnd
							? this.nextButton.removeAttribute('disabled')
							: (this.nextButton.disabled = true);
					}
				}
			}, 20);
		}
	}

	customElements.define('cc-carousel', CCCarousel);
}

class TestimonialsSection {
	constructor(section) {
		this.section = section;
		this.carousel = section.querySelector('cc-carousel');
		this.presentedTextContainer = section.querySelector('.testimonial-list__presented-text');
		this.list = section.querySelector('.testimonial-list');
		this.autoplay = this.list?.dataset.autoplay === 'true';
		this.autoplaySpeed = parseInt(this.list?.dataset.autoplaySpeed || '5000', 10);
		this.blockSelected = false;

		if (!this.carousel) {
			return;
		}

		this.carousel.get();
		this.onSelected = (event) => this.setPresentedText(event.detail.currentItem);
		this.carousel.addEventListener('cc-carousel:selected', this.onSelected);

		section.querySelectorAll('.testimonial__image').forEach((image) => {
			image.addEventListener('click', (event) => {
				const testimonial = event.currentTarget.closest('.testimonial');
				this.carousel.get().selectItem(testimonial);
			});
		});

		if (this.presentedTextContainer) {
			const firstItem = section.querySelector('.testimonial');
			if (firstItem) {
				this.setPresentedText(firstItem, true);
			}
		}

		if (this.autoplay && this.presentedTextContainer) {
			this.startAutoplay();
			section.addEventListener('mouseenter', () => this.stopAutoplay());
			section.addEventListener('mouseleave', () => {
				if (!this.blockSelected) {
					this.startAutoplay();
				}
			});
		}
	}

	setPresentedText(item, immediate) {
		if (!this.presentedTextContainer || !item) {
			return;
		}

		const sourceText = item.querySelector('.testimonial__text');
		const targetText = this.presentedTextContainer.querySelector('.testimonial__text');
		if (!sourceText || !targetText) {
			return;
		}

		if (immediate) {
			targetText.innerHTML = sourceText.innerHTML;
			this.presentedTextContainer.classList.add('testimonial-list__presented-text--reveal');
			return;
		}

		this.presentedTextContainer.classList.remove('testimonial-list__presented-text--reveal');
		clearTimeout(this.setPresentedTextTimeout1Id);
		clearTimeout(this.setPresentedTextTimeout2Id);

		const hideDuration = parseFloat(getComputedStyle(this.presentedTextContainer).getPropertyValue('--hide-duration')) || 0.75;

		this.setPresentedTextTimeout1Id = setTimeout(() => {
			targetText.innerHTML = sourceText.innerHTML;
			this.setPresentedTextTimeout2Id = setTimeout(() => {
				this.presentedTextContainer.classList.add('testimonial-list__presented-text--reveal');
			}, 50);
		}, hideDuration * 1000);
	}

	startAutoplay() {
		clearInterval(this.autoplayIntervalId);
		this.autoplayIntervalId = setInterval(() => {
			this.carousel.get().next();
		}, this.autoplaySpeed);
	}

	stopAutoplay() {
		clearInterval(this.autoplayIntervalId);
	}

	destroy() {
		this.stopAutoplay();
		this.carousel?.removeEventListener('cc-carousel:selected', this.onSelected);
	}
}

const testimonialsSections = new WeakMap();

function initTestimonialsSection(section) {
	if (!section || testimonialsSections.has(section)) {
		return;
	}
	testimonialsSections.set(section, new TestimonialsSection(section));
}

function destroyTestimonialsSection(section) {
	const instance = testimonialsSections.get(section);
	if (instance) {
		instance.destroy();
		testimonialsSections.delete(section);
	}
}

document.addEventListener('DOMContentLoaded', () => {
	document.querySelectorAll('.section-testimonials').forEach((section) => initTestimonialsSection(section));
});

document.addEventListener('shopify:section:load', (event) => {
	if (event.target.classList.contains('section-testimonials')) {
		initTestimonialsSection(event.target);
	}
});

document.addEventListener('shopify:section:unload', (event) => {
	if (event.target.classList.contains('section-testimonials')) {
		destroyTestimonialsSection(event.target);
	}
});

document.addEventListener('shopify:block:select', (event) => {
	const section = event.target.closest('.section-testimonials');
	const instance = testimonialsSections.get(section);
	if (!instance) {
		return;
	}
	instance.blockSelected = true;
	instance.stopAutoplay();
	instance.carousel.get().selectItem(event.target.closest('.testimonial') || event.target);
});

document.addEventListener('shopify:block:deselect', (event) => {
	const section = event.target.closest('.section-testimonials');
	const instance = testimonialsSections.get(section);
	if (!instance) {
		return;
	}
	instance.blockSelected = false;
	if (instance.autoplay) {
		instance.startAutoplay();
	}
});
